"use client";

import { useState, useEffect } from "react";
import ActiveCallDetail from "./ActiveCallDetail";
import { Button } from "../ui/button";
import { ButtonLoading } from "../base/ButtonLoading";
import { vapi } from "@/lib/vapiClient";
import { languages, agents } from "@/utils/agents";
import type { Agent, Lang } from "@/utils/agents";
import { isPublicKeyMissingError } from "@/utils/missingPublicKeyError";
import { useSearchParams } from 'next/navigation'
import axios from "axios";

type callComponentProps = {
  lang: Lang;
  firstname: string;
  emailaddress: string;
  className?: string;
};

const usePublicKeyInvalid = () => {
  const [showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage] = useState(false);

  // close public key invalid message after delay
  useEffect(() => {
    if (showPublicKeyInvalidMessage) {
      setTimeout(() => {
        setShowPublicKeyInvalidMessage(false);
      }, 3000);
    }
  }, [showPublicKeyInvalidMessage]);

  return {
    showPublicKeyInvalidMessage,
    setShowPublicKeyInvalidMessage,
  };
};

const PleaseSetYourPublicKeyMessage = () => {
  return (
    <div
      style={{
        position: "fixed",
        bottom: "25px",
        left: "25px",
        padding: "10px",
        color: "#fff",
        backgroundColor: "#f03e3e",
        borderRadius: "5px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
      }}
    >
      Is your Vapi Public Key missing? (recheck your code)
    </div>
  );
};

export default function CallComponent({ lang, firstname, emailaddress, className }: callComponentProps) {
  const [agentData, setAgentData] = useState<Agent | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [assistantIsSpeaking, setAssistantIsSpeaking] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const { showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage } = usePublicKeyInvalid();

  const searchParams = useSearchParams()
  const userEmail = searchParams.get('email')
  const userName = searchParams.get('name')
  const source = searchParams.get('utm_source')
  
  useEffect(() => {
    const fetchAgent = async () => {
      if (!languages.includes(lang)) {
        setAgentData(null);
      } else {
        setAgentData(agents[lang]);
      }
    };

    fetchAgent();
    vapi.on("call-start", () => {
      setConnecting(false);
      setConnected(true);
      setShowPublicKeyInvalidMessage(false);
    });

    vapi.on("call-end", () => {
      setConnecting(false);
      setConnected(false);
      setShowPublicKeyInvalidMessage(false);
    });

    vapi.on("speech-start", () => {
      setAssistantIsSpeaking(true);
    });

    vapi.on("speech-end", () => {
      setAssistantIsSpeaking(false);
    });

    vapi.on("volume-level", (level) => {
      setVolumeLevel(level);
    });

    vapi.on("error", (error) => {
      console.error(error);

      setConnecting(false);
      if (isPublicKeyMissingError({ vapiError: error })) {
        setShowPublicKeyInvalidMessage(true);
      }
    });

    return () => {
      vapi.stop(); // stop any ongoing call
      vapi.removeAllListeners(); // optional: remove event handlers
    };

    // we only want this to fire on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const startCall = () => {
    setConnecting(true);
    const response = vapi.start(agentData!.vapiId);
    response.then((res) => {

      if (res != null) {
        if (source!=null && userName!=null && userEmail!=null) {
        //console.log(userName, userEmail, res.id);
          axios.post("https://ntcadmin.dobeyond.ai/api/call-meta", { 
            "name": userName,
            "email": userEmail,
            "callId": res.id,
            "source": source
          })
        } else if (firstname!="" && emailaddress!="") {
          console.log(userName, userEmail, res.id);
          axios.post("https://ntcadmin.dobeyond.ai/api/call-meta", { 
            "name": firstname,
            "email": emailaddress,
            "callId": res.id,
            "source": "direct"
          })
        }
      }
      //console.log("call", res);
      //console.log(res.id) //<--- Call ID to be stored with UTM
    });
  };
  const endCall = () => {
    vapi.stop();
  };

  if (showPublicKeyInvalidMessage) {
    return PleaseSetYourPublicKeyMessage();
  }

  return (
    <div>
      {connected ? (
        <div
          className={`bg-muted rounded-lg shadow-md p-8 w-full max-w-md text-center space-y-6 ${className}`}
        >
          <ActiveCallDetail assistantIsSpeaking={assistantIsSpeaking} volumeLevel={volumeLevel} />
          <Button variant="destructive" className="w-full cursor-pointer" onClick={endCall}>
            End Call
          </Button>
        </div>
      ) : (
        <div>
          <ButtonLoading loading={connecting} onClick={startCall} propClassName="px-10 py-5">
            Talk to Luna
          </ButtonLoading>
        </div>
      )}
    </div>
  );
}
